import React, { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axiosInstance from "../../../../helpers/axiosInstance";
import useAuth from "../../../../hooks/useAuth";
import { date, dayMonth, money, monthYear, ordinal, parsePeriod } from "../../../../helpers/format";
import {
  Async,
  Banner,
  Button,
  Card,
  Confirm,
  Dialog,
  DownloadButton,
  Empty,
  Field,
  Page,
  Pill,
  SectionTitle,
  useAction,
  useFetch,
} from "../../../../shared/ui/ui";
import "./billing.css";

// Billing and subscriptions, as the app has it: the fund's SwitchFund plan,
// the way to change it, and SwitchFund's monthly bills to the fund.
//
// Every fund starts on Basic. Choosing a plan only asks for it — the
// SwitchFund team (a super-admin) verifies a request before it takes effect.
// The Billing and subscriptions section of a role decides who gets here:
// view to look, edit to change the plan or the billing.

const PAYMENT_METHODS = ["NEFT", "UPI", "IMPS", "RTGS", "Cheque", "Cash"];
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const useSubscription = () => {
  const { organisation } = useAuth();
  return useFetch(`/api/organisation/${organisation._id}/subscription`, { select: (body) => body.data });
};

const planOf = (subscription, key) => subscription.plans?.find((plan) => plan.key === key);
const nameOf = (subscription, key) => planOf(subscription, key)?.name || key;
const priceOf = (plan) => (plan?.price == null ? null : `${money(plan.price).replace(/\.00$/, "")} / ${plan.period || "month"}`);

/** The plan the fund has, and where the last request has got to. */
const StatusCard = ({ subscription }) => {
  const request = subscription.request;
  const asked = request ? nameOf(subscription, request.plan) : "";
  const current = nameOf(subscription, subscription.plan);

  let banner;
  if (request?.status === "pending") {
    banner = (
      <Banner tone="warn" title={`${asked} requested · Waiting for verification`}>
        Asked for{request.requested_by_name ? ` by ${request.requested_by_name}` : ""} on {date(request.requested_at)}. The
        SwitchFund team verifies every change; your fund stays on {current} until then.
      </Banner>
    );
  } else if (request?.status === "rejected") {
    banner = (
      <Banner tone="bad" title={`${asked} was not approved`}>
        {request.note || `Turned down on ${date(request.reviewed_at)}. Contact the SwitchFund team to find out why.`}
      </Banner>
    );
  } else if (request?.status === "approved" && request.plan === subscription.plan) {
    banner = (
      <Banner tone="good" title="Active · Verified">
        Verified by the SwitchFund team on {date(request.reviewed_at)}.
      </Banner>
    );
  } else {
    banner = (
      <Banner tone="good" title="Active">
        {subscription.plan_since ? `Since ${date(subscription.plan_since)}.` : "Every fund starts on Basic."}
      </Banner>
    );
  }

  return (
    <Card>
      <p className="sf-stat-label">Your fund's plan</p>
      <p className="sf-stat-value" data-testid="current-plan">
        {current}
      </p>
      {priceOf(planOf(subscription, subscription.plan)) ? (
        <p className="sf-muted" style={{ margin: "0.2rem 0 0" }}>
          {priceOf(planOf(subscription, subscription.plan))}
        </p>
      ) : null}
      {banner}
    </Card>
  );
};

/** Approve or turn down what is waiting — the SwitchFund team only. */
const ReviewCard = ({ subscription, onDone }) => {
  const { organisation } = useAuth();
  const { busy, run } = useAction();
  const [deciding, setDeciding] = useState(null);
  const [reason, setReason] = useState("");
  const plan = nameOf(subscription, subscription.request.plan);

  const decide = async () => {
    const approve = deciding === "approve";
    const ok = await run(
      () =>
        axiosInstance.post(`/api/organisation/${organisation._id}/subscription/review`, {
          decision: approve ? "approve" : "reject",
          note: approve ? undefined : reason.trim() || undefined,
        }),
      { done: approve ? `${organisation.name} is now on ${plan}.` : "Request turned down." }
    );
    if (ok) {
      setDeciding(null);
      onDone();
    }
  };

  return (
    <Card>
      <p className="sf-row-title">SwitchFund team</p>
      <p className="sf-muted">
        This fund has asked for {plan}. Approving moves it to {plan} straight away.
      </p>
      <div className="sf-chips">
        <Button onClick={() => setDeciding("reject")}>Turn down</Button>
        <Button variant="primary" onClick={() => setDeciding("approve")}>
          Approve
        </Button>
      </div>
      {deciding === "approve" ? (
        <Confirm title={`Approve ${plan} for ${organisation.name}?`} confirmLabel="Approve" busy={busy} onCancel={() => setDeciding(null)} onConfirm={decide}>
          {`${organisation.name} moves to ${plan} straight away.`}
        </Confirm>
      ) : null}
      {deciding === "reject" ? (
        <Confirm title={`Turn down ${plan}?`} confirmLabel="Turn down" danger busy={busy} onCancel={() => setDeciding(null)} onConfirm={decide}>
          <Field label="Reason (shown to the fund)">
            <textarea rows={3} value={reason} onChange={(event) => setReason(event.target.value)} autoFocus />
          </Field>
        </Confirm>
      ) : null}
    </Card>
  );
};

const invoiceTone = (invoice) => (invoice.status === "paid" ? "good" : invoice.status === "overdue" ? "bad" : "warn");
const invoiceLabel = (invoice) => (invoice.status === "paid" ? "Paid" : invoice.status === "overdue" ? "Overdue" : "Due");

/** The bills, newest first, each opening as the invoice itself. */
const Invoices = ({ billing }) => {
  const { organisation } = useAuth();
  const navigate = useNavigate();
  const invoices = useFetch(`/api/organisation/${organisation._id}/subscription/invoices`, { select: (body) => body.data });

  return (
    <Async state={invoices}>
      {(list) => {
        if (!list.length) {
          const first = parsePeriod(billing?.first_period);
          if (!first) return null;
          return (
            <Card>
              <p className="sf-muted" style={{ margin: 0 }}>
                No bills yet. The first, for {monthYear(first)}, is raised on {date(new Date(first.getFullYear(), first.getMonth() + 1, 1))}.
              </p>
            </Card>
          );
        }
        return (
          <Card flush>
            {list.map((invoice) => (
              <button key={invoice._id} className="sf-row sf-row-button" onClick={() => navigate(`/admin/billing/invoices/${invoice._id}`)}>
                <div className="sf-row-main">
                  <p className="sf-row-title">{monthYear(parsePeriod(invoice.period))}</p>
                  <p className="sf-row-sub">
                    {invoice.plan_name} · {invoice.status === "paid" ? `Paid ${date(invoice.paid_at)}` : `${invoice.status === "overdue" ? "Was due" : "Due"} ${date(invoice.due_at)}`}
                  </p>
                </div>
                <div className="sf-row-end">
                  <p className="sf-row-title">{money(invoice.amount).replace(/\.00$/, "")}</p>
                  <Pill tone={invoiceTone(invoice)}>{invoiceLabel(invoice)}</Pill>
                </div>
              </button>
            ))}
          </Card>
        );
      }}
    </Async>
  );
};

/** Billing and subscriptions: the plan on top, the bills below. */
export const BillingOverview = () => {
  const navigate = useNavigate();
  const subscription = useSubscription();

  return (
    <Page title="Billing and subscriptions">
      <Async state={subscription}>
        {(data) => {
          const current = planOf(data, data.plan)?.price ?? 0;
          const canUpgrade = (data.plans || []).some((plan) => (plan.price ?? 0) > current);
          const handlesBilling = data.can_request || data.can_review;
          const billing = data.billing;
          return (
            <>
              <SectionTitle title="Subscription" />
              <StatusCard subscription={data} />
              {data.can_request ? (
                <Button variant="primary" block onClick={() => navigate("/admin/billing/plans")}>
                  {canUpgrade ? "Upgrade plan" : "Change plan"}
                </Button>
              ) : (
                <Button block onClick={() => navigate("/admin/billing/plans")}>
                  See all plans
                </Button>
              )}
              {data.can_review && data.request?.status === "pending" ? (
                <div style={{ marginTop: "0.75rem" }}>
                  <ReviewCard subscription={data} onDone={subscription.reload} />
                </div>
              ) : null}

              {handlesBilling ? (
                <>
                  <SectionTitle
                    title="Billing"
                    action={
                      <Button variant="text" onClick={() => navigate("/admin/billing/settings")}>
                        ⚙ Billing settings
                      </Button>
                    }
                  />
                  <p className="sf-muted">
                    {billing
                      ? `Raised on the 1st for the month before · Due on the ${ordinal(billing.due_day)} · Sent to ${billing.email}`
                      : "Billing is not set up yet. It is asked for when you submit a plan, or you can set it up in Billing settings."}
                  </p>
                  <Invoices billing={billing} />
                </>
              ) : null}
            </>
          );
        }}
      </Async>
    </Page>
  );
};

/** Due day, billing email and payment method, shared by the request and the settings. */
const BillingFields = ({ value, onChange, disabled, error }) => (
  <>
    <Field label="Bill due day">
      <select value={value.due_day} disabled={disabled} onChange={(event) => onChange({ ...value, due_day: Number(event.target.value) })}>
        {Array.from({ length: 28 }, (_, index) => index + 1).map((day) => (
          <option key={day} value={day}>
            {ordinal(day)} of the month
          </option>
        ))}
      </select>
    </Field>
    <Field label="Billing email" help="Every bill is emailed here." error={error}>
      <input type="email" value={value.email} disabled={disabled} onChange={(event) => onChange({ ...value, email: event.target.value })} />
    </Field>
    <Field label="Payment method">
      <select value={value.payment_method} disabled={disabled} onChange={(event) => onChange({ ...value, payment_method: event.target.value })}>
        {PAYMENT_METHODS.map((method) => (
          <option key={method}>{method}</option>
        ))}
      </select>
    </Field>
    <p className="sf-muted">A bill is raised on the 1st of every month for the month before, and is due on the {ordinal(value.due_day)}.</p>
  </>
);

const startingBilling = (subscription, auth) => ({
  due_day: subscription.billing?.due_day || 10,
  email: subscription.billing?.email || auth?.user?.email || "",
  payment_method: subscription.billing?.payment_method || "NEFT",
});

/** Every plan, and the way to ask for another. */
export const ChangePlan = () => {
  const navigate = useNavigate();
  const { auth, organisation } = useAuth();
  const subscription = useSubscription();
  const [selected, setSelected] = useState(null);
  const [comparing, setComparing] = useState(false);
  const [asking, setAsking] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const { busy, run } = useAction();

  const submit = async (data) => {
    if (!EMAIL.test(asking.email.trim())) {
      setEmailError("Enter the email address bills should go to");
      return;
    }
    const ok = await run(
      () =>
        axiosInstance.post(`/api/organisation/${organisation._id}/subscription`, {
          plan: selected,
          billing: { ...asking, email: asking.email.trim() },
        }),
      { done: "Request sent. The SwitchFund team will verify it." }
    );
    if (ok) navigate("/admin/billing");
  };

  return (
    <Page title="Change plan" back>
      <Async state={subscription}>
        {(data) => {
          // Open on the plan already asked for, or else — on Basic — the one
          // the catalogue recommends, the change most likely wanted.
          const initial = data.request?.status === "pending" ? data.request.plan : data.plan === "basic" ? data.plans.find((plan) => plan.recommended)?.key || data.plan : data.plan;
          const choice = selected ?? initial;
          const reason =
            choice === data.plan
              ? `Your fund is already on ${nameOf(data, choice)}.`
              : data.request?.status === "pending" && data.request.plan === choice
              ? `${nameOf(data, choice)} is already waiting to be verified.`
              : null;
          return (
            <>
              <p className="sf-muted">The major features of each plan.</p>
              {data.plans.map((plan) => {
                const isSelected = data.can_request && plan.key === choice;
                return (
                  <div
                    key={plan.key}
                    className={`sf-card billing-plan ${isSelected ? "billing-plan-selected" : ""} ${data.can_request ? "billing-plan-selectable" : ""}`}
                    onClick={() => data.can_request && setSelected(plan.key)}
                    role={data.can_request ? "radio" : undefined}
                    aria-checked={isSelected}
                  >
                    <div className="billing-plan-head">
                      {data.can_request ? <span className="billing-radio">{isSelected ? "◉" : "○"}</span> : null}
                      <strong className="billing-plan-name">{plan.name}</strong>
                      <span style={{ flex: 1 }} />
                      {plan.key === data.plan ? <Pill tone="good">Current</Pill> : null}
                      {data.request?.status === "pending" && data.request.plan === plan.key ? <Pill tone="warn">Requested</Pill> : null}
                      {plan.recommended && plan.key !== data.plan ? <Pill tone="strong">Recommended</Pill> : null}
                    </div>
                    {plan.price != null ? (
                      <p className="billing-price">
                        {money(plan.price).replace(/\.00$/, "")} <span className="sf-muted">/ {plan.period || "month"}</span>
                      </p>
                    ) : null}
                    {plan.tagline ? <p className="sf-muted">{plan.tagline}</p> : null}
                    {plan.includes ? <p className="billing-includes">Everything in {plan.includes}, plus:</p> : null}
                    <ul className="billing-highlights">
                      {plan.highlights.map((line) => (
                        <li key={line}>✓ {line}</li>
                      ))}
                    </ul>
                  </div>
                );
              })}

              <Button block onClick={() => setComparing((open) => !open)}>
                {comparing ? "Hide the comparison" : "Compare every feature"}
              </Button>
              {comparing ? <Comparison subscription={data} /> : null}

              <div style={{ marginTop: "1.25rem" }}>
                {data.can_request ? (
                  <>
                    <Button
                      variant="primary"
                      block
                      disabled={Boolean(reason)}
                      onClick={() => {
                        setSelected(choice);
                        setEmailError(null);
                        setAsking(startingBilling(data, auth));
                      }}
                    >
                      Submit
                    </Button>
                    <p className="sf-muted" style={{ textAlign: "center" }}>
                      {reason || "The SwitchFund team verifies the change before it takes effect."}
                    </p>
                  </>
                ) : (
                  <p className="sf-muted" style={{ textAlign: "center" }}>
                    Only your fund's administrators can change its plan.
                  </p>
                )}
              </div>

              {asking ? (
                <Dialog
                  title={`Submit request for ${nameOf(data, choice)}?`}
                  onClose={busy ? undefined : () => setAsking(null)}
                  actions={
                    <>
                      <Button variant="text" onClick={() => setAsking(null)} disabled={busy}>
                        Cancel
                      </Button>
                      <Button variant="primary" busy={busy} onClick={() => submit(data)}>
                        Submit
                      </Button>
                    </>
                  }
                >
                  {priceOf(planOf(data, choice)) ? <p>{`${nameOf(data, choice)} is ${priceOf(planOf(data, choice))}.`}</p> : null}
                  <p className="sf-muted">
                    The SwitchFund team verifies every plan change. {organisation.name} stays on {nameOf(data, data.plan)} until they do, and you
                    can follow the status on Billing and subscriptions.
                  </p>
                  {data.request?.status === "pending" ? (
                    <p className="sf-muted">This replaces the request for {nameOf(data, data.request.plan)} that is waiting.</p>
                  ) : null}
                  <h4 style={{ margin: "1rem 0 0.75rem" }}>Billing</h4>
                  <BillingFields
                    value={asking}
                    onChange={(next) => {
                      setEmailError(null);
                      setAsking(next);
                    }}
                    error={emailError}
                  />
                </Dialog>
              ) : null}
            </>
          );
        }}
      </Async>
    </Page>
  );
};

/** Every feature, plan by plan, grouped as the catalogue groups them. */
const Comparison = ({ subscription }) => {
  const areas = useMemo(() => {
    const grouped = new Map();
    for (const feature of subscription.features || []) {
      if (!grouped.has(feature.area)) grouped.set(feature.area, []);
      grouped.get(feature.area).push(feature);
    }
    return [...grouped.entries()];
  }, [subscription]);
  const cell = (value) => (value === "Yes" ? "✓" : value === "No" ? "—" : value);
  return (
    <div className="billing-compare">
      <table className="sf-table">
        <thead>
          <tr>
            <th>Feature</th>
            {subscription.plans.map((plan) => (
              <th key={plan.key} className="sf-num">
                {plan.name}
                {plan.price != null ? <div className="sf-muted">{money(plan.price).replace(/\.00$/, "")}</div> : null}
              </th>
            ))}
          </tr>
        </thead>
        {areas.map(([area, features]) => (
          <tbody key={area}>
            <tr>
              <th colSpan={subscription.plans.length + 1} className="billing-compare-area">
                {area}
              </th>
            </tr>
            {features.map((feature) => (
              <tr key={feature.feature}>
                <td>{feature.feature}</td>
                {subscription.plans.map((plan) => (
                  <td key={plan.key} className="sf-num">
                    {cell(feature[plan.key] || "No")}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
};

/** Change the due day, the billing email or the payment method. */
export const BillingSettings = () => {
  const navigate = useNavigate();
  const { auth, organisation } = useAuth();
  const subscription = useSubscription();
  const [value, setValue] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const { busy, run } = useAction();

  return (
    <Page title="Billing settings" back>
      <Async state={subscription}>
        {(data) => {
          const current = value || startingBilling(data, auth);
          const now = new Date();
          const save = async () => {
            if (!EMAIL.test(current.email.trim())) {
              setEmailError("Enter the email address bills should go to");
              return;
            }
            const ok = await run(
              () => axiosInstance.post(`/api/organisation/${organisation._id}/subscription/billing`, { ...current, email: current.email.trim() }),
              { done: "Billing settings saved." }
            );
            if (ok) navigate("/admin/billing");
          };
          return (
            <Card>
              <p className="sf-muted">
                {data.billing
                  ? `Billing since ${date(data.billing.started_at)}.`
                  : `Billing is not set up yet. Saving starts it: the first bill, for ${monthYear(new Date(now.getFullYear(), now.getMonth() + 1, 1))}, is raised on ${date(new Date(now.getFullYear(), now.getMonth() + 2, 1))}.`}
              </p>
              <BillingFields
                value={current}
                disabled={!data.can_request || busy}
                error={emailError}
                onChange={(next) => {
                  setEmailError(null);
                  setValue(next);
                }}
              />
              {data.can_request ? (
                <Button variant="primary" block busy={busy} onClick={save}>
                  Save
                </Button>
              ) : (
                <p className="sf-muted" style={{ textAlign: "center" }}>
                  Only your fund's administrators can change its billing.
                </p>
              )}
            </Card>
          );
        }}
      </Async>
    </Page>
  );
};

/** One month's bill, laid out as the PDF is. */
export const PlanInvoice = () => {
  const { invoiceId } = useParams();
  const { organisation } = useAuth();
  const subscription = useSubscription();
  const invoices = useFetch(`/api/organisation/${organisation._id}/subscription/invoices`, { select: (body) => body.data });
  const [confirming, setConfirming] = useState(false);
  const { busy, run } = useAction();

  return (
    <Async state={invoices}>
      {(list) => {
        const invoice = list.find((candidate) => candidate._id === invoiceId);
        if (!invoice) {
          return (
            <Page title="Invoice" back>
              <Empty title="Invoice not found">It may have been removed.</Empty>
            </Page>
          );
        }
        const isTeam = subscription.data?.can_review;
        const paid = invoice.status === "paid";
        const plan = `${invoice.plan_name} Monthly Plan`;
        const markPaid = async () => {
          const ok = await run(() => axiosInstance.post(`/api/organisation/${organisation._id}/subscription/invoices/${invoice._id}/paid`), {
            done: `${invoice.number} marked as paid.`,
          });
          if (ok) {
            setConfirming(false);
            invoices.reload();
          }
        };
        return (
          <Page title={invoice.number} back>
            <div className="plan-invoice">
              <div className="plan-invoice-band" />
              <div className="plan-invoice-head">
                <div>
                  <h2>{invoice.issuer?.name || "SwitchFund"}</h2>
                  {invoice.issuer?.email ? <p className="sf-muted">{invoice.issuer.email}</p> : null}
                </div>
                <div className="plan-invoice-meta">
                  <h1>INVOICE</h1>
                  <p>Invoice #: {invoice.number}</p>
                  <p>Date issued: {date(invoice.issued_at)}</p>
                  <p>Due date: {date(invoice.due_at)}</p>
                  <Pill tone={invoiceTone(invoice)}>{paid ? `Paid ${date(invoice.paid_at)}` : invoiceLabel(invoice)}</Pill>
                </div>
              </div>
              <div className="plan-invoice-parties">
                <div>
                  <h5>BILLED TO</h5>
                  <p>{invoice.billed_to?.name}</p>
                  <p>{invoice.billed_to?.email}</p>
                </div>
                <div>
                  <h5>SUBSCRIPTION PLAN</h5>
                  <p>{plan}</p>
                  <p>Billing cycle: Monthly</p>
                  <p>Payment method: {invoice.payment_method}</p>
                </div>
              </div>
              <table className="plan-invoice-table">
                <thead>
                  <tr>
                    <th>DESCRIPTION</th>
                    <th className="sf-num">QTY</th>
                    <th className="sf-num">PRICE</th>
                    <th className="sf-num">AMOUNT</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <strong>{plan} Subscription</strong>
                      <div className="sf-muted">
                        {dayMonth(invoice.period_start)} – {date(invoice.period_end)}
                      </div>
                    </td>
                    <td className="sf-num">1</td>
                    <td className="sf-num">{money(invoice.amount)}</td>
                    <td className="sf-num">
                      <strong>{money(invoice.amount)}</strong>
                    </td>
                  </tr>
                  <tr className="plan-invoice-sum">
                    <td colSpan={3} className="sf-num sf-muted">
                      Subtotal
                    </td>
                    <td className="sf-num">{money(invoice.amount)}</td>
                  </tr>
                  <tr className="plan-invoice-sum">
                    <td colSpan={3} className="sf-num sf-muted">
                      Tax (0%)
                    </td>
                    <td className="sf-num">{money(0)}</td>
                  </tr>
                </tbody>
              </table>
              <div className="plan-invoice-total">
                <span>{paid ? "Total paid" : "Total due"}</span>
                <span>{money(invoice.amount)}</span>
              </div>
              <div className="plan-invoice-notes">
                <h5>Notes</h5>
                <p className="sf-muted">Thank you for your subscription! This invoice covers the billing period above. Your plan renews automatically each month.</p>
              </div>
            </div>
            <div style={{ display: "grid", gap: "0.75rem", marginTop: "1rem" }}>
              <DownloadButton
                block
                label="Download invoice"
                fileName={`${invoice.number}.pdf`}
                url={`/api/organisation/${organisation._id}/subscription/invoices/${invoice._id}/pdf`}
              />
              {isTeam && !paid ? (
                <Button variant="primary" block onClick={() => setConfirming(true)}>
                  Mark as paid
                </Button>
              ) : null}
            </div>
            {confirming ? (
              <Confirm title={`Mark ${invoice.number} as paid?`} confirmLabel="Mark as paid" busy={busy} onCancel={() => setConfirming(false)} onConfirm={markPaid}>
                {`${money(invoice.amount)} for ${monthYear(parsePeriod(invoice.period))}. Only do this once the money has arrived.`}
              </Confirm>
            ) : null}
          </Page>
        );
      }}
    </Async>
  );
};
